/**
 * Grouping a deal's files for the deal page: by what each file is (its
 * classified document type) or by where it sits (its top-level sub-folder).
 */
import {
	compareDocumentTypes,
	documentTypeLabel,
} from "../config/documentTypes";
import type { LibraryFile } from "../hooks/useFileLibrary";

export type GroupBy = "type" | "folder";

export interface FileGrouping {
	key: string;
	label: string;
	count: number;
}

/** Files directly in the deal's root folder. */
export const ROOT_FOLDER_KEY = "__root__";

export const groupKey = (file: LibraryFile, by: GroupBy): string =>
	by === "type" ? file.documentType : (file.folderPath[0] ?? ROOT_FOLDER_KEY);

/**
 * `folders` names the deal's top-level sub-folders, so that grouping by
 * folder lists an empty one too (it can still be deleted, or filled).
 */
export function groupFiles(
	files: LibraryFile[],
	by: GroupBy,
	folders: string[] = [],
): FileGrouping[] {
	const counts = new Map<string, number>();
	if (by === "folder") for (const name of folders) counts.set(name, 0);
	for (const file of files) {
		const key = groupKey(file, by);
		counts.set(key, (counts.get(key) ?? 0) + 1);
	}
	const keys = Array.from(counts.keys());
	if (by === "type") {
		keys.sort(compareDocumentTypes);
	} else {
		keys.sort((a, b) =>
			a === ROOT_FOLDER_KEY ? -1 : b === ROOT_FOLDER_KEY ? 1 : a.localeCompare(b),
		);
	}
	return keys.map((key) => ({
		key,
		label:
			by === "type"
				? documentTypeLabel(key)
				: key === ROOT_FOLDER_KEY
					? "Deal folder"
					: key,
		count: counts.get(key) ?? 0,
	}));
}
